import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Sparkles, MoreHorizontal, Power, Loader2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  plansApi, Plan, UpdatePlanBody, platformTenantsApi, PlatformTenant,
  BackendPlanTier, tierToLabel, statusToLabel,
} from "@/lib/platform-api";
import { ApiError } from "@/lib/api";

const TIERS: BackendPlanTier[] = ["BASIC", "PRO", "PREMIUM", "ENTERPRISE"];
const planStyle: Record<BackendPlanTier, string> = {
  BASIC: "border-muted-foreground/30 bg-muted text-foreground",
  PRO: "border-info/30 bg-info/10 text-info",
  PREMIUM: "border-primary/30 bg-primary/10 text-primary",
  ENTERPRISE: "border-accent/30 bg-accent/10 text-accent",
};
const statusStyle = (s: string) =>
  s === "ACTIVE" ? "border-success/30 bg-success/10 text-success"
    : s === "SUSPENDED" ? "border-destructive/30 bg-destructive/10 text-destructive"
    : "border-warning/30 bg-warning/10 text-warning";
const money = (v: string) => `RM ${Number(v).toLocaleString()}`;

export default function Subscriptions() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [editing, setEditing] = useState<Plan | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([plansApi.list(), platformTenantsApi.list({ pageSize: 200 })])
      .then(([p, t]) => { if (!cancelled) { setPlans(p); setTenants(t.data); } })
      .catch((e) => { if (!cancelled) toast.error(e instanceof ApiError ? e.message : "Failed to load subscriptions"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const tenantsOnTier = (tier: BackendPlanTier) => tenants.filter((t) => t.plan.tier === tier).length;

  const changePlan = async (t: PlatformTenant, tier: BackendPlanTier) => {
    try {
      await platformTenantsApi.changePlan(t.id, tier);
      toast.success(`${t.name} moved to ${tierToLabel(tier)}`);
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e instanceof ApiError ? e.message : "Failed to change plan"); }
  };
  const toggleSuspend = async (t: PlatformTenant) => {
    try {
      if (t.status === "SUSPENDED") { await platformTenantsApi.reactivate(t.id); toast.success(`${t.name} reactivated`); }
      else { await platformTenantsApi.suspend(t.id); toast.warning(`${t.name} suspended`); }
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e instanceof ApiError ? e.message : "Action failed"); }
  };

  if (loading) {
    return <div className="flex h-96 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Plans */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p) => (
          <Card key={p.id} className={cn("relative overflow-hidden", p.highlight && "ring-2 ring-primary/40 shadow-elegant")}>
            {p.highlight && (
              <div className="absolute right-3 top-3">
                <Badge className="bg-gradient-to-r from-accent to-primary text-white"><Sparkles className="mr-1 h-3 w-3" /> Most popular</Badge>
              </div>
            )}
            <CardHeader>
              <CardTitle className="font-display text-xl">{p.name}</CardTitle>
              <CardDescription>{p.targetAudience ?? "—"}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="font-display text-3xl font-bold">{money(p.pricePerMonth)}<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <div className="text-xs text-muted-foreground">Up to {p.maxUsers.toLocaleString()} users · {p.maxSites} sites</div>
              </div>
              <div className="flex items-center justify-between rounded-lg border bg-secondary/40 p-2.5 text-xs">
                <span className="text-muted-foreground">Active tenants</span>
                <Badge variant="outline" className={planStyle[p.tier]}>{tenantsOnTier(p.tier)}</Badge>
              </div>
              {p.features.length > 0 && (
                <ul className="space-y-1.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" /><span>{f}</span></li>
                  ))}
                </ul>
              )}
              <Button variant="outline" size="sm" className="w-full" onClick={() => setEditing(p)}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Configure plan
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="border-b pb-3">
          <CardTitle className="text-base">Tenant subscriptions</CardTitle>
          <CardDescription>Commercial state across all tenants ({tenants.length})</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead><TableHead>Current plan</TableHead>
                <TableHead className="text-right">Users</TableHead><TableHead>Renewal</TableHead>
                <TableHead>Status</TableHead><TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="text-sm font-medium">{t.name}<span className="ml-1.5 text-xs text-muted-foreground">{t.code}</span></TableCell>
                  <TableCell><Badge variant="outline" className={planStyle[t.plan.tier]}>{tierToLabel(t.plan.tier)}</Badge></TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{t.counts.activeUsers.toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.renewalDate ? new Date(t.renewalDate).toLocaleDateString() : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={statusStyle(t.status)}>{statusToLabel(t.status)}</Badge></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel className="text-xs">Change plan</DropdownMenuLabel>
                        {TIERS.filter((tier) => tier !== t.plan.tier).map((tier) => (
                          <DropdownMenuItem key={tier} onClick={() => changePlan(t, tier)}>{tierToLabel(tier)}</DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className={t.status === "SUSPENDED" ? "" : "text-destructive focus:text-destructive"} onClick={() => toggleSuspend(t)}>
                          <Power className="mr-2 h-3.5 w-3.5" /> {t.status === "SUSPENDED" ? "Reactivate" : "Suspend"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PlanEditDialog plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setReloadKey((k) => k + 1); }} />
    </div>
  );
}

function PlanEditDialog({ plan, onClose, onSaved }: { plan: Plan | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<UpdatePlanBody>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) setForm({ name: plan.name, pricePerMonth: Number(plan.pricePerMonth), maxUsers: plan.maxUsers, maxSites: plan.maxSites, targetAudience: plan.targetAudience ?? "", highlight: plan.highlight });
  }, [plan]);

  const save = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      await plansApi.update(plan.id, form);
      toast.success(`${plan.name} plan updated`);
      onSaved();
    } catch (e) { toast.error(e instanceof ApiError ? e.message : "Failed to update plan"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Configure {plan?.name} plan</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Name</Label><Input className="mt-1" value={form.name ?? ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Price / month (RM)</Label><Input type="number" className="mt-1" value={form.pricePerMonth ?? 0} onChange={(e) => setForm((f) => ({ ...f, pricePerMonth: Number(e.target.value) }))} /></div>
            <div><Label className="text-xs">Max users</Label><Input type="number" className="mt-1" value={form.maxUsers ?? 0} onChange={(e) => setForm((f) => ({ ...f, maxUsers: Number(e.target.value) }))} /></div>
            <div><Label className="text-xs">Max sites</Label><Input type="number" className="mt-1" value={form.maxSites ?? 0} onChange={(e) => setForm((f) => ({ ...f, maxSites: Number(e.target.value) }))} /></div>
          </div>
          <div><Label className="text-xs">Target audience</Label><Input className="mt-1" value={form.targetAudience ?? ""} onChange={(e) => setForm((f) => ({ ...f, targetAudience: e.target.value }))} /></div>
          <div className="flex items-center justify-between pt-1"><Label className="text-sm">Highlight as "Most popular"</Label><Switch checked={Boolean(form.highlight)} onCheckedChange={(v) => setForm((f) => ({ ...f, highlight: v }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
